/**
 * Portal guide — registry schema.
 *
 * One entry per guide target (section, tab, action …). The data lives as JSON in
 * `src/guide/registry/<section>.json` and is assembled in `src/guide/featureRegistry.ts`.
 * The section files are the hand-maintained source (de and en). Whoever adds, renames or removes a
 * section, tab or create button updates them and then syncs into tri-server
 * (`npm run guide:sync`). `scripts/portal-guide-normalize-seed.js` and
 * `scripts/portal-guide-merge-i18n.js` are the one-off migration tools that produced the first
 * version from the seed data; they document the provenance and are not run again.
 */
import { z } from 'zod';

export type FeatureKind = 'page' | 'panel' | 'tab' | 'action' | 'dialog' | 'menu';

/** Role ids — exactly these five (roleAccessCore.ts: roles[] for root/administrator, roleAssignments for the other three). */
export type RoleId = 'root' | 'administrator' | 'customerAdministrator' | 'technicalAdministrator' | 'billingAdministrator';

/** Exactly the visibility flags from src/hooks/roleAccessCore.ts (RoleAccess) — no others. */
export type AccessFlag =
  | 'canSeeCompanyProfile' | 'canSeeTeamManagement' | 'canManageRoles' | 'canSeeBilling' | 'canSeeStatistics'
  | 'canSeeOrders' | 'canPlaceOrders' | 'canSeeApiKeys' | 'canSeeAgent' | 'canSeeProjects' | 'canSeeProductManagement'
  | 'canSeeFileManagement' | 'canSeeOperations' | 'canSeeDevelopment' | 'canSeeDataManagement' | 'canDeleteProcessInstances';

/** Booking/tenant condition beyond the role. */
export type Booking =
  | 'tender'               // tenant has a tool from MCP server 69fc5126b9e23f4aedbdb22c (TENDER_MCP_SERVER_ID)
  | 'operatorObligations'  // order with offer 6a255f8040d2b1e58e36c478 or provider tenant S8QW-G8R2-9QLC
  | 'productManagement'    // order with offer 6a303278f82f726ee11a3718 or provider tenant
  | 'cbos'                 // tenant in CBOS_CUSTOMERS (src/cbos/cbosAccess.ts), currently ['KJPS-UVAB-ISBR']
  | 'administration';      // administration: role root only (configuration.shouldSeeAdministration) — modeled as a booking because it gates the whole route

export interface Localized { de: string; en: string }
export interface LocalizedList { de: string[]; en: string[] }

/**
 * State conditions a create button can impose — a closed list, because the registry and the
 * panel hook must mean the same condition.
 *
 * - `anyRole`            — the tenant has at least one role (create checklist: without a role
 *                          the checklist would get an empty executing role)
 * - `tenantLegalPerson`  — a company is on file for the tenant (create product: it is entered
 *                          as the manufacturer)
 */
export type GuideActionState = 'anyRole' | 'tenantLegalPerson';

/**
 * Prefill via address (stage 2, contract §7.5). If an entry carries `guideAction`, the assistant
 * may extend the address with `&guide=<id>&<field>=<value>…`; the `useGuideAction` hook in the
 * panel reads it, opens the create dialog prefilled and immediately clears the parameters from
 * the address. Prefilling is not writing — the human still presses the button.
 */
export interface GuideAction {
  /**
   * Value of the `?guide=` address parameter — the same dot notation as the registry ids, with a
   * descriptive suffix (e.g. `settings.alerts.create`). Must be unique across the whole registry,
   * because it is the only way the hook recognizes that it is meant. Convention (guard (n)):
   * `guideAction.id` is the same id as `guideId` — highlight target and prefill key denote the
   * same button; two spellings for it would be a silent source of errors.
   */
  id: string;
  /**
   * Exactly the form fields the address may set: **the names of the address parameters**, which
   * also name the dialog's form fields (`<route>&guide=<id>&<field>=<value>`). Which state
   * variable the panel keeps behind them is recorded in `evidence.component` — it is often named
   * differently (`name` → `newKeyName` in key management). Anything not listed here is silently
   * dropped. Never a secret: addresses end up in browser history, the `Referer` and logs.
   */
  fields: string[];
  /**
   * Subset of `fields` that the panel keeps as a **list** (e.g. the keywords of a search
   * profile). The address carries exactly one value per field, so lists arrive as a
   * comma-separated list (`GUIDE_ACTION_LIST_SEPARATOR`) and are split when set. Without this
   * marker every consumer would have to guess whether to set a string or an array.
   */
  listFields?: string[];
  /**
   * Subset of `fields` containing personal data (email, first/last name of a third person).
   * They are allowed — the invitation needs them — but until the hook's
   * `setSearchParams(replace)` they sit in browser history and access logs. The marker is
   * mandatory (guard in the schema) so nobody adds them by accident.
   */
  personalFields?: string[];
  /**
   * State condition the panel hook checks **before** opening. The hook calls the opener directly
   * and thereby bypasses the button — if the button's `disabled=` condition is not met, the user
   * would otherwise land in a form they cannot complete. If the condition is not met, the hook
   * opens nothing and leaves the panel's existing hint text in place.
   */
  requiresState?: GuideActionState;
}

export interface PortalFeature {
  /** stable, hierarchical, dot notation: ^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9]+)*$ ; prefix = route area (settings|entity|administration|chat|cbos|shell|auth|misc) */
  id: string;
  kind: FeatureKind;
  /** id of the parent element; must exist; roots have none */
  parent?: string;
  name: Localized;
  /** search terms (without repeating the name itself — the schema rejects the duplicate); en may be empty if de is empty */
  synonyms: LocalizedList;
  /**
   * Canonical address: path + query, starts with '/'. Placeholders in curly braces: {selectedEntity}, {project}.
   * NO prose, no alternatives ("bzw.", "wortgleich auch"), no [optional] brackets. Exactly one address.
   * Tabs that are not reachable via address get the section's address plus deeplinkHint.
   */
  route: string;
  /** placeholder names the caller MUST fill — exactly the {…} in route, same order */
  params: string[];
  /** further query parameters the page understands (table page, filters…) — names only, informational */
  optionalParams: string[];
  /** roles allowed to see/use the entry; [] = any signed-in role. System admin (root/administrator) always passes. */
  roles: RoleId[];
  /** exactly one flag from roleAccessCore if the section is gated by it */
  accessFlag?: AccessFlag;
  booking?: Booking;
  /** prose evidence of the condition (for maintainers, not for the user) */
  gate: string;
  description: Localized;
  /** 1–5 steps, imperative, labels verbatim as in de.json/en.json */
  howTo: LocalizedList;
  prerequisites: LocalizedList;
  /** number + title of the heading in docs/benutzerdokumentation.md, e.g. "8.13 Agentendienste" — without "##", without location. Absent if the docs do not cover the section. */
  docAnchor?: string;
  /** data-guide-id of the target element (stage 2); empty in stage 1 */
  guideId?: string;
  /** hint when switching tab/panel loses the address or the address does not carry everything */
  deeplinkHint?: Localized;
  /** prefill via `?guide=` (stage 2) — only on entries whose dialog actually has the fields */
  guideAction?: GuideAction;
  /** evidence: component location(s), i18n key — for maintainers only */
  evidence: { component: string; i18nKey?: string };
}

/* ------------------------------------------------------------------ Vocabularies */

export const FEATURE_KINDS = ['page', 'panel', 'tab', 'action', 'dialog', 'menu'] as const;

export const ROLE_IDS = [
  'root',
  'administrator',
  'customerAdministrator',
  'technicalAdministrator',
  'billingAdministrator',
] as const;

export const ACCESS_FLAGS = [
  'canSeeCompanyProfile', 'canSeeTeamManagement', 'canManageRoles', 'canSeeBilling', 'canSeeStatistics',
  'canSeeOrders', 'canPlaceOrders', 'canSeeApiKeys', 'canSeeAgent', 'canSeeProjects', 'canSeeProductManagement',
  'canSeeFileManagement', 'canSeeOperations', 'canSeeDevelopment', 'canSeeDataManagement', 'canDeleteProcessInstances',
] as const;

export const BOOKINGS = ['tender', 'operatorObligations', 'productManagement', 'cbos', 'administration'] as const;

/** Allowed id prefixes — they correspond to the section files (auth.json holds auth.* and misc.*). */
export const ID_PREFIXES = ['settings', 'entity', 'administration', 'chat', 'cbos', 'shell', 'auth', 'misc'] as const;

/** Pattern for `id`. No `g` flag: `RegExp.test` would otherwise be stateful. */
export const ID_PATTERN = /^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9]+)*$/;

/**
 * Pattern for `route` — an allowlist, not "starts with /".
 * The route IS the source of the link (contract §4 emits `[<Name> öffnen](<route>)`), and
 * `markdownLinkComponent.tsx` classifies `//host` as external, or on modifier-click passes `/\host`
 * through to the browser, which turns it into `//host` — both land on a foreign domain while the
 * link looks portal-internal. So only this is allowed:
 * exactly one leading slash, path characters, curly placeholders, a simple query.
 */
export const ROUTE_PATTERN = /^\/(?!\/)[A-Za-z0-9._~\-/{}]*(?:\?[A-Za-z0-9._~\-=&{}%]*)?$/;

/** Characters that have no business in an address (backslash, brackets, quotes, router stars). */
export const ROUTE_FORBIDDEN = /[\\()<>"'`*[\]|\s]/;

/**
 * Query names that must never appear in `params`/`optionalParams`: sign-in credentials and
 * OAuth redirect parameters. Per contract §1, `params` are values the caller MUST fill —
 * a language model with page context would thus write a credential into a visible link.
 */
export const FORBIDDEN_PARAM_NAMES = [
  'password', 'token', 'secret', 'apiKey', 'api_key', 'client_secret', 'client_id',
  'redirect_uri', 'scope', 'state', 'code', 'code_challenge', 'code_challenge_method',
] as const;

/**
 * User-facing texts must be inert: per contract §4 they flow verbatim into `answerMarkdown`, and
 * `ChatMessage.tsx` renders with `rehypeRaw` without `rehype-sanitize`. So raw HTML (including
 * `<id>` placeholders — the renderer swallows them), markdown links, absolute addresses,
 * `javascript:`/`data:` targets and code fences are forbidden.
 */
export const TEXT_NOT_INERT = /<[a-zA-Z/!]|\]\(|https?:\/\/|(?:javascript|vbscript|data):(?=[^\s])|```/;

/**
 * Source code locations belong in `gate`/`evidence`, not in user-facing texts.
 * Mirror of `scripts/portal-guide-text.js` (which does the cleaning).
 */
export const TEXT_CODE_REFERENCE =
  /\bsrc\/[A-Za-z0-9_./-]+|\b[A-Za-z0-9_@][A-Za-z0-9_@.-]*\.(?:tsx|ts|jsx|js|mjs|cjs|json|md)\b|[\s("„]:\d/;

/** A user-facing text is valid if it is neither active nor a code reference. */
export function textProblem(value: string): string | null {
  if (TEXT_NOT_INERT.test(value)) {
    return 'darf kein HTML, keinen Markdown-Link, keine absolute Adresse und keinen Code-Zaun enthalten';
  }
  if (TEXT_CODE_REFERENCE.test(value)) {
    return 'darf keine Quelltext-Fundstelle enthalten (die gehört in gate/evidence)';
  }
  return null;
}

/* ------------------------------------------------- Prefill: id and field names */

/** Pattern for `guideAction.id` — the same dot notation as `id`, at least two segments. */
export const GUIDE_ACTION_ID_PATTERN = /^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9]+)+$/;

/** Pattern for a field name in `guideAction.fields` (name of the state variable in the panel). */
export const GUIDE_ACTION_FIELD_PATTERN = /^[a-z][a-zA-Z0-9]*$/;

/** Maximum number of prefillable fields per action — more would not fit in a readable address. */
export const GUIDE_ACTION_MAX_FIELDS = 8;

/**
 * Words that name a secret. Checked **word by word** (camelCase split, simple plural stripped),
 * not as a substring: `apiKey`, `accessTokens` and `otpCode` are blocked, but the legitimate
 * search profile field `keywords` is not — a keyword is not a key.
 */
export const GUIDE_ACTION_SECRET_WORDS = ['password', 'token', 'secret', 'key', 'credential', 'otp'] as const;

/**
 * Additional substring bolt for the words that occur in no harmless field name.
 *
 * `key` is listed here with the exception `keyword`: the word-by-word check alone depends on the
 * camelCase boundary and therefore let through exactly the spellings a human would also pick for
 * a secret — `apikey`, `privatekey`, `sshkey`, `userkey` yielded no word "key".
 * With `key(?!word)` they are caught, while the search profile's keyword field (`keywords`,
 * `keyword`) passes. There is no field carrying `key` in another harmless sense —
 * and if there were one, a named exception here would be the right place for it.
 */
export const GUIDE_ACTION_SECRET_SUBSTRING = /password|secret|credential|token|key(?!word)/i;

/** Query parameters that carry a tab in the address: `tab`, and `section` for the orders tabs. */
export const TAB_ADDRESS_PATTERN = /[?&](?:tab|section)=/;

/**
 * Names the address already carries itself: the hook's marker (`guide`) and the section/tab
 * selection (`panel`, `tab`). A field with the same name would overwrite them — the hook would no
 * longer find itself or would land in the wrong section.
 */
export const GUIDE_ACTION_RESERVED_PARAMS = ['guide', 'panel', 'tab'] as const;

/** Separator for a field from `listFields` — an address carries exactly one value per field. */
export const GUIDE_ACTION_LIST_SEPARATOR = ',';

/** Maximum length of a prefilled value (contract §7.5). */
export const GUIDE_ACTION_VALUE_MAX_LENGTH = 200;

/**
 * Field names that carry personal data and therefore must be listed in `personalFields`.
 * Deliberately narrow: the list catches the invitation's fields; it is not a privacy classifier.
 */
export const GUIDE_ACTION_PERSONAL_FIELD =
  /^(?:e?mail|firstName|lastName|givenName|familyName|fullName|phone|mobile|telephone|birthDate)$/;

/** State conditions that `guideAction.requiresState` may carry. */
export const GUIDE_ACTION_STATES = ['anyRole', 'tenantLegalPerson'] as const;

/**
 * A field name splits into its words at camelCase boundaries and at digits.
 *
 * Two boundaries, not one: lower→upper (`apiKey` → `api|Key`) **and** the acronym boundary
 * upper+upper→upper+lower (`myAPIKey` → `my|API|Key`). Without the second, `myAPIKey` stayed the
 * word `apikey`, which is on no blocklist — and a secret would slip past the word check.
 */
export function guideActionFieldWords(field: string): string[] {
  return field
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((word) => word !== '');
}

/** Why a field name must not go into the address — `null` if it is fine. */
export function guideActionFieldProblem(field: string): string | null {
  if (!GUIDE_ACTION_FIELD_PATTERN.test(field)) {
    return 'ist kein Bezeichner (kleiner Anfangsbuchstabe, danach Buchstaben und Ziffern)';
  }
  if (GUIDE_ACTION_SECRET_SUBSTRING.test(field)) {
    return 'benennt ein Geheimnis und darf nie in einer Adresse stehen';
  }
  const words = guideActionFieldWords(field);
  const secrets = GUIDE_ACTION_SECRET_WORDS as readonly string[];
  const hit = words.find(
    (word) => secrets.includes(word) || (word.endsWith('s') && secrets.includes(word.slice(0, -1))),
  );
  if (hit) return `benennt ein Geheimnis („${hit}") und darf nie in einer Adresse stehen`;
  if ((GUIDE_ACTION_RESERVED_PARAMS as readonly string[]).includes(field)) {
    return `ist ein reservierter Adressparameter (${GUIDE_ACTION_RESERVED_PARAMS.join(', ')})`;
  }
  if ((FORBIDDEN_PARAM_NAMES as readonly string[]).includes(field)) {
    // Same blocklist as for `params`/`optionalParams`: a field ends up in exactly the address
    // the list was written against — credentials and OAuth redirect parameters.
    return 'ist ein Credential bzw. ein OAuth-Weiterleitungsparameter und darf nicht in einer Adresse stehen';
  }
  return null;
}

/**
 * Query names a `route` already carries itself — a field with the same name would overwrite them.
 * Plain string work, because `route` may contain placeholders in curly braces that
 * `URLSearchParams` would choke on.
 */
export function routeQueryNames(route: string): string[] {
  const query = route.indexOf('?') >= 0 ? route.slice(route.indexOf('?') + 1) : '';
  if (query === '') return [];
  return query
    .split('&')
    .map((pair) => pair.split('=')[0])
    .filter((name) => name !== '');
}

/**
 * A value as it may go into the address: without curly braces and truncated to
 * `GUIDE_ACTION_VALUE_MAX_LENGTH`.
 *
 * The braces are removed because `useGuideNavigate.parseInternalHref` rejects any address with
 * `{`/`}` — that bolt targets unfilled registry placeholders, but would also hit an alert name
 * like "Batterie {Groß-Gerau}" and make the button silently do nothing. Server and panel hook
 * use the same function so the rule lives in one place.
 */
export function sanitizeGuideActionValue(value: string): string {
  return value.replace(/[{}]/g, '').slice(0, GUIDE_ACTION_VALUE_MAX_LENGTH);
}

/** Placeholders in `route`, in order. */
export function routePlaceholders(route: string): string[] {
  const found: string[] = [];
  const pattern = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;
  let match = pattern.exec(route);
  while (match !== null) {
    found.push(match[1]);
    match = pattern.exec(route);
  }
  return found;
}

/* ---------------------------------------------- Guard: type and vocabulary are congruent */

type Assert<T extends true> = T;
type SameSet<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
export type _FeatureKindsMatch = Assert<SameSet<FeatureKind, (typeof FEATURE_KINDS)[number]>>;
export type _RoleIdsMatch = Assert<SameSet<RoleId, (typeof ROLE_IDS)[number]>>;
export type _AccessFlagsMatch = Assert<SameSet<AccessFlag, (typeof ACCESS_FLAGS)[number]>>;
export type _BookingsMatch = Assert<SameSet<Booking, (typeof BOOKINGS)[number]>>;

/* ------------------------------------------------------------------ zod schema */

export interface PortalFeatureSchemaOptions {
  /**
   * true = English texts are mandatory (parity) — this is how `portalFeatureSchema` has run since
   * the translation pass. false allows all `en` fields except `name.en` to be empty; that is only
   * meant for intermediate states before `scripts/portal-guide-merge-i18n.js` has run.
   */
  requireEnglish: boolean;
}

const localizedBase = z.object({ de: z.string(), en: z.string() });
const localizedListBase = z.object({ de: z.array(z.string()), en: z.array(z.string()) });


/**
 * Replacement for `z.enum(<constant list>)`: TypeScript 4.9.5 cannot read zod v4's declaration of
 * `z.enum` (const type parameters, TS 5+ only) and reports "Expected 0 arguments, but got 1" in
 * the build. `z.custom` checks the same value and carries the list's type.
 */
function oneOf<T extends string>(values: readonly T[], label: string) {
  return z.custom<T>(
    (value) => typeof value === 'string' && (values as readonly string[]).includes(value),
    { message: `${label} muss einer von ${values.join(', ')} sein` },
  );
}

export function buildPortalFeatureSchema(opts: PortalFeatureSchemaOptions) {
  const { requireEnglish } = opts;

  const localizedText = (label: string) =>
    localizedBase.superRefine((value, ctx) => {
      if (value.de.trim() === '') {
        ctx.addIssue({ code: 'custom', message: `${label}.de darf nicht leer sein` });
      }
      if (requireEnglish && value.en.trim() === '') {
        ctx.addIssue({ code: 'custom', message: `${label}.en darf nicht leer sein` });
      }
      (['de', 'en'] as const).forEach((lang) => {
        const problem = textProblem(value[lang]);
        if (problem) ctx.addIssue({ code: 'custom', message: `${label}.${lang} ${problem}` });
      });
    });

  const localizedSteps = (label: string, min: number, max: number) =>
    localizedListBase.superRefine((value, ctx) => {
      if (value.de.length < min || value.de.length > max) {
        ctx.addIssue({ code: 'custom', message: `${label}.de braucht ${min}–${max} Einträge, hat ${value.de.length}` });
      }
      if (value.de.some((step) => step.trim() === '')) {
        ctx.addIssue({ code: 'custom', message: `${label}.de enthält einen leeren Eintrag` });
      }
      if (requireEnglish && value.de.length !== value.en.length) {
        ctx.addIssue({
          code: 'custom',
          message: `${label}: de/en müssen gleich lang sein (de ${value.de.length}, en ${value.en.length})`,
        });
      }
      if (requireEnglish && value.en.some((step) => step.trim() === '')) {
        ctx.addIssue({ code: 'custom', message: `${label}.en enthält einen leeren Eintrag` });
      }
      (['de', 'en'] as const).forEach((lang) => {
        value[lang].forEach((step, index) => {
          const problem = textProblem(step);
          if (problem) ctx.addIssue({ code: 'custom', message: `${label}.${lang}[${index}] ${problem}` });
        });
      });
    });

  return z
    .object({
      id: z
        .string()
        .regex(ID_PATTERN, 'id verletzt das Punktnotations-Muster')
        .refine(
          (id) => (ID_PREFIXES as readonly string[]).includes(id.split('.')[0]),
          { message: `id-Präfix muss eines von ${ID_PREFIXES.join('|')} sein` },
        ),
      kind: oneOf(FEATURE_KINDS, 'kind'),
      parent: z.string().regex(ID_PATTERN).optional(),
      name: localizedText('name'),
      synonyms: localizedListBase.superRefine((value, ctx) => {
        (['de', 'en'] as const).forEach((lang) => {
          value[lang].forEach((word, index) => {
            const problem = textProblem(word);
            if (problem) ctx.addIssue({ code: 'custom', message: `synonyms.${lang}[${index}] ${problem}` });
          });
        });
        if (requireEnglish && value.de.length > 0 && value.en.length === 0) {
          ctx.addIssue({ code: 'custom', message: 'synonyms.en fehlt, obwohl synonyms.de gefüllt ist' });
        }
        if (requireEnglish && value.en.some((word) => word.trim() === '')) {
          ctx.addIssue({ code: 'custom', message: 'synonyms.en enthält einen leeren Eintrag' });
        }
      }),
      route: z
        .string()
        .regex(ROUTE_PATTERN, 'route muss eine portalinterne Adresse sein (ein führendes /, keine //, kein \\, keine Router-Muster)'),
      params: z.array(z.string()),
      optionalParams: z.array(z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, 'optionalParams sind reine Bezeichner')),
      roles: z.array(oneOf(ROLE_IDS, 'roles[]')),
      accessFlag: oneOf(ACCESS_FLAGS, 'accessFlag').optional(),
      booking: oneOf(BOOKINGS, 'booking').optional(),
      gate: z.string(),
      description: localizedText('description'),
      howTo: localizedSteps('howTo', 1, 5),
      prerequisites: localizedSteps('prerequisites', 0, 10),
      docAnchor: z.string().min(1).optional(),
      guideId: z.string().optional(),
      deeplinkHint: localizedBase.optional(),
      guideAction: z
        .object({
          id: z.string(),
          fields: z.array(z.string()),
          listFields: z.array(z.string()).optional(),
          personalFields: z.array(z.string()).optional(),
          requiresState: oneOf(GUIDE_ACTION_STATES, 'guideAction.requiresState').optional(),
        })
        .superRefine((value, ctx) => {
          if (!GUIDE_ACTION_ID_PATTERN.test(value.id)) {
            ctx.addIssue({
              code: 'custom',
              path: ['id'],
              message: 'guideAction.id braucht die Punktnotation der Registry-Ids mit Suffix (z. B. settings.alerts.create)',
            });
          }
          if (!(ID_PREFIXES as readonly string[]).includes(value.id.split('.')[0])) {
            ctx.addIssue({
              code: 'custom',
              path: ['id'],
              message: `guideAction.id-Präfix muss eines von ${ID_PREFIXES.join('|')} sein`,
            });
          }
          if (value.fields.length < 1 || value.fields.length > GUIDE_ACTION_MAX_FIELDS) {
            ctx.addIssue({
              code: 'custom',
              path: ['fields'],
              message: `guideAction.fields braucht 1–${GUIDE_ACTION_MAX_FIELDS} Einträge, hat ${value.fields.length}`,
            });
          }
          if (new Set(value.fields).size !== value.fields.length) {
            ctx.addIssue({ code: 'custom', path: ['fields'], message: 'guideAction.fields enthält Dubletten' });
          }
          value.fields.forEach((field, index) => {
            const problem = guideActionFieldProblem(field);
            if (problem) {
              ctx.addIssue({ code: 'custom', path: ['fields', index], message: `guideAction.fields[${index}] „${field}" ${problem}` });
            }
          });
          // `listFields` and `personalFields` are properties of the fields, not a second list:
          // a name that is not in `fields` marks nothing and still promises something.
          ([['listFields', value.listFields], ['personalFields', value.personalFields]] as const).forEach(
            ([label, marked]) => {
              if (!marked) return;
              if (new Set(marked).size !== marked.length) {
                ctx.addIssue({ code: 'custom', path: [label], message: `guideAction.${label} enthält Dubletten` });
              }
              marked.forEach((field, index) => {
                if (!value.fields.includes(field)) {
                  ctx.addIssue({
                    code: 'custom',
                    path: [label, index],
                    message: `guideAction.${label}[${index}] „${field}" steht nicht in fields`,
                  });
                }
              });
            },
          );
          // Personal fields are allowed, but never unnoticed: until the hook clears the address
          // via replace, they sit in browser history and access logs.
          const unmarkedPersonal = value.fields.filter(
            (field) => GUIDE_ACTION_PERSONAL_FIELD.test(field) && !(value.personalFields ?? []).includes(field),
          );
          if (unmarkedPersonal.length > 0) {
            ctx.addIssue({
              code: 'custom',
              path: ['personalFields'],
              message: `guideAction.personalFields muss die personenbezogenen Felder nennen (fehlt: ${unmarkedPersonal.join(', ')})`,
            });
          }
        })
        .optional(),
      evidence: z.object({ component: z.string().min(1), i18nKey: z.string().optional() }),
    })
    .superRefine((feature, ctx) => {
      const placeholders = routePlaceholders(feature.route);
      if (placeholders.join('|') !== feature.params.join('|')) {
        ctx.addIssue({
          code: 'custom',
          path: ['params'],
          message: `params müssen exakt die Platzhalter aus route sein (route: [${placeholders.join(', ')}], params: [${feature.params.join(', ')}])`,
        });
      }
      if (/\bbzw\.|wortgleich/.test(feature.route) || ROUTE_FORBIDDEN.test(feature.route)) {
        ctx.addIssue({
          code: 'custom',
          path: ['route'],
          message: 'route enthält Prosa, Alternativen, Leerzeichen oder ein verbotenes Zeichen (\\ ( ) < > " \' ` * [ ])',
        });
      }
      if (feature.route.indexOf('//') >= 0) {
        ctx.addIssue({ code: 'custom', path: ['route'], message: 'route darf kein // enthalten (das wäre eine fremde Domain)' });
      }
      const forbiddenParam = feature.params
        .concat(feature.optionalParams)
        .find((name) => (FORBIDDEN_PARAM_NAMES as readonly string[]).includes(name));
      if (forbiddenParam) {
        ctx.addIssue({
          code: 'custom',
          path: ['params'],
          message: `„${forbiddenParam}" ist ein Credential bzw. ein OAuth-Weiterleitungsparameter und darf nicht in params/optionalParams stehen`,
        });
      }
      if (feature.optionalParams.some((name) => !/^[a-z][a-zA-Z0-9_]*$/.test(name))) {
        ctx.addIssue({
          code: 'custom',
          path: ['optionalParams'],
          message: 'optionalParams sind kleingeschriebene Query-Namen — ein großgeschriebenes Wort stammt aus einer Formularfeld-Beschreibung',
        });
      }
      // Compare per language only: a German synonym equal to the English name is intended
      // (users type English product terms into the German UI).
      (['de', 'en'] as const).forEach((lang) => {
        const ownName = feature.name[lang].trim().toLowerCase();
        if (feature.synonyms[lang].some((word) => word.trim() !== '' && word.trim().toLowerCase() === ownName)) {
          ctx.addIssue({
            code: 'custom',
            path: ['synonyms'],
            message: `synonyms.${lang} wiederholt den Namen des Eintrags`,
          });
        }
      });
      // `section=` counts as well: the tabs of „Bestellungen" (orders, billing, statistics) sit in
      // `?section=` because `?tab=` belongs to the statistics' own tabs (SettingsPage.tsx).
      if (feature.kind === 'tab' && !TAB_ADDRESS_PATTERN.test(feature.route) && !feature.deeplinkHint) {
        ctx.addIssue({
          code: 'custom',
          path: ['deeplinkHint'],
          message: 'ein Reiter ohne tab= bzw. section= in der Adresse braucht einen deeplinkHint (Vertrag §1)',
        });
      }
      if (feature.guideAction && feature.kind !== 'action' && feature.kind !== 'dialog') {
        // What gets prefilled is a create dialog. `dialog` and `action` are the two kinds under
        // which the registry lists such a button (ApiKeyManagement and the search profile are
        // `dialog` because the button opens a dialog; the rest are `action`).
        ctx.addIssue({
          code: 'custom',
          path: ['guideAction'],
          message: `guideAction gehört an einen Eintrag der Art action oder dialog, nicht an ${feature.kind}`,
        });
      }
      if (feature.guideAction) {
        // The address is built as `<route>&guide=<id>&<field>=<value>`. A field name that the
        // route or `params`/`optionalParams` already carry would overwrite the existing value —
        // `?panel=agent&…&panel=Batterie` becomes a section that does not exist.
        const taken = new Set(
          routeQueryNames(feature.route).concat(feature.params).concat(feature.optionalParams),
        );
        const clashing = feature.guideAction.fields.filter((field) => taken.has(field));
        if (clashing.length > 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['guideAction', 'fields'],
            message: `guideAction.fields kollidiert mit Namen, die die Adresse schon führt: ${clashing.join(', ')}`,
          });
        }
        if (feature.guideId && feature.guideAction.id !== feature.guideId) {
          // One id per target: `guideId` highlights the button, `guideAction.id` prefills its
          // dialog. Two spellings would be the costliest mistake — they work where they happen
          // to match and silently fail everywhere else.
          ctx.addIssue({
            code: 'custom',
            path: ['guideAction', 'id'],
            message: `guideAction.id („${feature.guideAction.id}") muss der guideId des Eintrags entsprechen („${feature.guideId}")`,
          });
        }
      }
      if (feature.parent === feature.id) {
        ctx.addIssue({ code: 'custom', path: ['parent'], message: 'parent darf nicht auf den Eintrag selbst zeigen' });
      }
      if (new Set(feature.optionalParams).size !== feature.optionalParams.length) {
        ctx.addIssue({ code: 'custom', path: ['optionalParams'], message: 'optionalParams enthält Dubletten' });
      }
      if (feature.optionalParams.some((name) => feature.params.includes(name))) {
        ctx.addIssue({ code: 'custom', path: ['optionalParams'], message: 'optionalParams überschneidet sich mit params' });
      }
      if (new Set(feature.roles).size !== feature.roles.length) {
        ctx.addIssue({ code: 'custom', path: ['roles'], message: 'roles enthält Dubletten' });
      }
      if (feature.deeplinkHint) {
        (['de', 'en'] as const).forEach((lang) => {
          const problem = textProblem(feature.deeplinkHint![lang]);
          if (problem) ctx.addIssue({ code: 'custom', path: ['deeplinkHint'], message: `deeplinkHint.${lang} ${problem}` });
        });
        if (feature.deeplinkHint.de.trim() === '') {
          ctx.addIssue({ code: 'custom', path: ['deeplinkHint'], message: 'deeplinkHint.de darf nicht leer sein' });
        }
        if (requireEnglish && feature.deeplinkHint.de.trim() !== '' && feature.deeplinkHint.en.trim() === '') {
          ctx.addIssue({
            code: 'custom',
            path: ['deeplinkHint'],
            message: 'deeplinkHint.en fehlt, obwohl deeplinkHint.de gesetzt ist',
          });
        }
      }
    });
}

/**
 * Phase 2 (since 22.09.2026, translation pass complete): English texts are mandatory.
 * Parity means: `name.en`/`description.en` not empty, `howTo.en` and `prerequisites.en` as long
 * as their German counterparts, `synonyms.en` filled whenever `synonyms.de` is, and
 * `deeplinkHint.en` set whenever `deeplinkHint.de` is set.
 * The lenient variant remains available via `buildPortalFeatureSchema({ requireEnglish: false })` —
 * for intermediate states of new entries still awaiting translation.
 */
export const portalFeatureSchema = buildPortalFeatureSchema({ requireEnglish: true });

export const portalRegistrySchema = z.array(portalFeatureSchema);
